import { ApiPaths, getFullUrl } from "./api";
import { emitTokenExpired } from "./authEvents";

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

export function getAccessTokenFromStorage() {
	return localStorage.getItem("access_token");
}

export function getRefreshTokenFromStorage() {
	return localStorage.getItem("refresh_token");
}

export function storeTokens(access: string, refresh: string) {
	// Clear provider selection when storing new tokens (new login session)
	localStorage.removeItem("selected_provider_id");
	// Clear anonymous trip planner token when user logs in
	localStorage.removeItem("tripPlannerAnonymousId");
	localStorage.setItem("access_token", access);
	localStorage.setItem("refresh_token", refresh);
}

export function clearTokens() {
	localStorage.removeItem("access_token");
	localStorage.removeItem("refresh_token");
	// Clear provider selection on logout
	localStorage.removeItem("selected_provider_id");
}

/** How long we wait for the server to acknowledge a logout before giving up. */
const REVOKE_TIMEOUT_MS = 3000;

/**
 * Revoke the refresh token server-side (DELETE /api/v1/logout).
 *
 * The endpoint authorizes on the *refresh* token, not the access token, so that
 * is what we send. Best-effort by design: clearing local tokens must never be
 * blocked by a network failure, so every error is swallowed. Call this only on
 * an explicit user logout — not when tokens are already known to be invalid.
 */
export async function revokeRefreshToken(): Promise<void> {
	// Read the token synchronously, before the first `await`. Callers fire this
	// without awaiting and clear storage on the very next line, so this read must
	// stay above any suspension point or the token would already be gone.
	const refresh = getRefreshTokenFromStorage();
	if (!refresh) return;

	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), REVOKE_TIMEOUT_MS);

	try {
		await fetch(getFullUrl(ApiPaths.LOGOUT), {
			method: "DELETE",
			headers: {
				Authorization: `Bearer ${refresh}`,
				"Content-Type": "application/json",
			},
			signal: controller.signal,
		});
	} catch {
		// Offline, aborted, or server error — the local session is cleared regardless.
	} finally {
		clearTimeout(timeout);
	}
}

export async function getValidAccessToken(): Promise<string | null> {
	const access = getAccessTokenFromStorage();
	const refresh = getRefreshTokenFromStorage();
	if (!access || !refresh) return null;

	const [, payloadBase64] = access.split(".");
	const payload = JSON.parse(atob(payloadBase64));
	const isExpired = payload.exp * 1000 < Date.now();

	if (!isExpired) return access;

	if (!isRefreshing) {
		isRefreshing = true;
		refreshPromise = fetch(getFullUrl(ApiPaths.REFRESH_TOKEN), {
			method: "POST",
			headers: {
				Authorization: `Bearer ${refresh}`,
				"Content-Type": "application/json",
			},
		})
			.then((res) => {
				if (!res.ok) throw new Error("Refresh failed");

				return res.json();
			})
			.then((data) => {
				storeTokens(data.access_token, data.refresh_token);
				return data.access_token;
			})
			.catch(() => {
				clearTokens();
				emitTokenExpired();
				return null;
			})
			.finally(() => {
				isRefreshing = false;
				refreshPromise = null;
			});
	}

	return refreshPromise;
}

export async function isAuthenticated(): Promise<boolean> {
	const token = await getValidAccessToken();

	return token != null;
}

export async function decodeJWT() {
	try {
		const token = await getValidAccessToken();
		if (!token) return null;

		const payload = token.split(".")[1];
		return JSON.parse(atob(payload));
	} catch {
		return null;
	}
}

export async function getUserId() {
	const data = await decodeJWT();
	if (!data) return null;

	return data.user_id;
}

export type UserRole = "provider" | "administrator" | "owner" | null;

export async function getUserRole(): Promise<UserRole> {
	const data = await decodeJWT();
	if (!data) return null;

	return data.role || null;
}

// Clear anonymous token when user logs in (to use authenticated identity instead)
export function clearAnonymousToken(): void {
	localStorage.removeItem("tripPlannerAnonymousId");
}
